import { ReactNode, useEffect, useRef } from 'react';
import styles from './modal.module.css';
import { MaterialSymbolIcon } from '@/components/material-symbol-link';
import { Button } from '@/components/button';
import clsx from 'clsx';
import { useMediaQuery } from '@/hooks/use-media-query';

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

export function ModalBody({ children, className }: ModalBodyProps) {
  const isMobile = useMediaQuery('--screen-md');
  return (
    <div className={clsx(styles.modalBody, isMobile ? 'body-2' : 'body-1', className)}>
      {children}
    </div>
  );
}

interface ModalButtonContainerProps {
  children: ReactNode;
  className?: string;
}

export function ModalButtonContainer({ children, className }: ModalButtonContainerProps) {
  return <div className={clsx(styles.buttonContainer, className)}>{children}</div>;
}

interface ModalProps {
  isOpen: boolean;
  closeOnOutsideClick?: boolean;
  hideCloseButton?: boolean;
  children: ReactNode;
  className?: string;
  onClose?: () => void;
}

export function Modal({
  isOpen,
  closeOnOutsideClick = false,
  children,
  className,
  hideCloseButton = false,
  onClose,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (closeOnOutsideClick && event.target === dialogRef.current) {
      onClose?.();
      dialogRef.current?.close();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDialogElement>) => {
    if (event.key === 'Escape') {
      onClose?.();
      dialogRef.current?.close();
    }
  };

  useEffect(() => {
    if (!dialogRef.current) return;

    if (isOpen) {
      dialogRef.current.showModal();
    } else {
      dialogRef.current.close();
    }
  }, [isOpen, dialogRef]);

  return (
    //eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={styles.modal}
      onCancel={onClose}
      onKeyDown={handleKeyDown}
      onClick={handleBackdropClick}
      onDrop={(e) => e.stopPropagation()}
    >
      <div className={clsx(styles.modalContainer, className)}>
        {!hideCloseButton && (
          <Button
            variant="none"
            className={styles.closeButton}
            onClick={() => {
              onClose?.();
              dialogRef.current?.close();
            }}
          >
            <MaterialSymbolIcon name="close" />
          </Button>
        )}
        {children}
      </div>
    </dialog>
  );
}
